import { useMutation } from "@tanstack/react-query";
import { format as formatDate } from "date-fns";
import FileSaver from "file-saver";
import type { CardTransactionFilters } from "Components/CardTransactions/utils/cardTransactionFilterUtils";
import { cardFiltersToQueryParams } from "Components/CardTransactions/utils/cardTransactionFilterUtils";
import {
  getApiUrl,
  getCardTransactionsExportPath,
  getQueryParamsString,
  postCardTransactionsExport,
  TransactionExportFormat,
} from "Infrastructure/Api/Api";
import { processResponseWithApiProblemDetails } from "Infrastructure/Api/ApiUtils";
import { JWT_KEY } from "Utils/AuthUtils";
import { getLocalStorageItem } from "Utils/Storage/LocalStorage/SecureLocalStorage";

// Derived from the generated enum (shared with the A2A transaction export) so the set of formats has a
// single source of truth. Email is excluded — it goes through the POST path, not a file download.
export type CardExportDownloadFormat = Exclude<
  TransactionExportFormat,
  "Email"
>;
type SupportedFileType = "csv" | "pdf" | "xlsx";

const FILE_TYPE_TO_FILE_EXTENSION: Record<
  CardExportDownloadFormat,
  SupportedFileType
> = {
  Csv: "csv",
  Pdf: "pdf",
  Xlsx: "xlsx",
};

type ExportParams = {
  format: TransactionExportFormat;
  filters: CardTransactionFilters;
  email?: string;
};

const fetchCardTransactionsExport = async (
  filters: CardTransactionFilters,
  exportFormat: CardExportDownloadFormat,
) => {
  const token = getLocalStorageItem(JWT_KEY);
  if (!token) {
    throw new Error("Authentication required for export");
  }

  const apiUrl = getApiUrl();
  const endpointPath = getCardTransactionsExportPath();
  const queryParams = cardFiltersToQueryParams(filters);
  const queryString = getQueryParamsString({
    Status: queryParams.status,
    DateFrom: queryParams.dateFrom,
    DateTo: queryParams.dateTo,
    PayPointPublicID: queryParams.payPointPublicID,
    EmployeePublicID: queryParams.employeePublicID,
    IsRefundOrCancellation: queryParams.isRefundOrCancellation,
  });

  const formatQuery = queryString
    ? `${queryString}&Format=${exportFormat}`
    : `?Format=${exportFormat}`;

  const response = await fetch(`${apiUrl}${endpointPath}${formatQuery}`, {
    method: "GET",
    mode: "cors",
    headers: { Authorization: token },
  });

  if (!response.ok) {
    throw new Error(`Export failed: ${response.status}`);
  }

  return response;
};

export const useExportCardTransactionsMutation = () =>
  useMutation({
    mutationFn: async ({ format, filters, email }: ExportParams) => {
      const queryParams = cardFiltersToQueryParams(filters);

      if (format === TransactionExportFormat.Email) {
        const sendEmail = processResponseWithApiProblemDetails(() =>
          postCardTransactionsExport({
            format,
            email,
            dateFrom: queryParams.dateFrom,
            dateTo: queryParams.dateTo,
            payPointPublicID: queryParams.payPointPublicID,
            employeePublicID: queryParams.employeePublicID,
            status: queryParams.status,
            isRefundOrCancellation: queryParams.isRefundOrCancellation,
          }),
        );

        const result = await sendEmail();
        return { success: true, isEmail: true, emailSent: result?.emailSent };
      }

      const response = await fetchCardTransactionsExport(filters, format);
      const blob = await response.blob();
      const fileExtension = FILE_TYPE_TO_FILE_EXTENSION[format];
      // FR-EXP-2 filename pattern: karetni_transakce_[dateFrom]_[dateTo] with one-sided fallbacks
      // (today's date only when the filter has no period at all).
      const from = queryParams.dateFrom
        ? formatDate(new Date(queryParams.dateFrom), "yyyy-MM-dd")
        : undefined;
      const to = queryParams.dateTo
        ? formatDate(new Date(queryParams.dateTo), "yyyy-MM-dd")
        : undefined;
      const rangePart =
        from && to
          ? `${from}_${to}`
          : (from ?? to ?? formatDate(new Date(), "yyyy-MM-dd"));
      const fileName = `karetni_transakce_${rangePart}.${fileExtension}`;
      FileSaver.saveAs(blob, fileName);

      return { success: true, isEmail: false };
    },
  });
